import { t } from "@lingui/macro";
import React from "react";

import { Theme } from "../../../theme/restyle/theme";
import { Button } from "../../controls/Button/Button";
import { IconProps } from "../../primitive";
import { Box, HStack, Text } from "../../primitive";

type InfoBannerActionProps = {
  label: string;
  handler: () => void;
};

type HStackProps = React.ComponentProps<typeof HStack>;

export type InfoBannerColorThemes =
  | "NEUTRAL"
  | "SUCCESS"
  | "WARNING"
  | "ERROR"
  | "BLUE"
  | "PURPLE"
  | "PINK"
  | "WHITE"
  | "A"
  | "B"
  | "C"
  | "D"
  | "E"
  | "F";

type WidthProp = Pick<HStackProps, "width">;

type InfoBannerPropsBasic = {
  message: string;
  title?: string;
  leadingIcon?: React.ComponentType<IconProps>;
  action?: InfoBannerActionProps;
  secondaryText?: string;
  colorTheme: InfoBannerColorThemes;
  type?: "FILL" | "CARD";
  testID?: string;
};

export type InfoBannerProps = InfoBannerPropsBasic & WidthProp;

export const InfoBanner = ({
  message,
  title,
  leadingIcon: LeadingIcon,
  action,
  secondaryText,
  colorTheme,
  type = "CARD",
  width,
  testID,
}: InfoBannerProps) => {
  const { backgroundColor, iconColor } = colorMap[colorTheme];

  return (
    <HStack
      width={width}
      height={type === "FILL" ? 48 : "auto"}
      p={2}
      paddingLeft={4}
      backgroundColor={backgroundColor}
      borderRadius={type === "CARD" ? "2" : "0"}
      alignItems={"center"}
      justifyContent={"space-around"}
      testID={testID}
    >
      <HStack justifyContent="flex-start" flex={1} alignItems="center">
        {LeadingIcon ? (
          <Box mr={2}>
            <LeadingIcon color={iconColor} />
          </Box>
        ) : null}
        <Box flex={1} pr={2}>
          {title ? (
            <Text variant={"mainTiny"} color="text-primary">
              {title}
            </Text>
          ) : null}
          <Text variant={"mainTinyMedium"} color="text-primary">
            {message}
          </Text>
        </Box>
        {secondaryText || action ? (
          <HStack alignItems="center" justifyContent="flex-end" gap={2}>
            {secondaryText ? (
              <Box>
                <Text
                  textAlign="right"
                  variant={"mainTinyMedium"}
                  color="text-primary"
                >
                  {secondaryText}
                </Text>
              </Box>
            ) : null}
            {action ? (
              <Button
                aria-label={t`Info-Banner-Action-${action.label}`}
                accessibilityLabel={t`Info-Banner-Action-${action.label}`}
                size={"tiny"}
                variant={"white"}
                onPress={action.handler}
              >
                {action.label}
              </Button>
            ) : null}
          </HStack>
        ) : null}
      </HStack>
    </HStack>
  );
};

type BackgroundColorProps = Pick<HStackProps, "backgroundColor">;
type InfoBannerColorMapValue = BackgroundColorProps & {
  iconColor: keyof Theme["colors"];
};

type InfoBannerColorMap = Record<
  InfoBannerColorThemes,
  InfoBannerColorMapValue
>;

const colorMap: InfoBannerColorMap = {
  SUCCESS: {
    backgroundColor: "surface-status-success-subtler",
    iconColor: "icon-on-color-success",
  },
  WARNING: {
    backgroundColor: "surface-status-warning-subtler",
    iconColor: "icon-on-color-warning",
  },
  ERROR: {
    backgroundColor: "surface-status-error-subtler",
    iconColor: "icon-on-color-error",
  },
  BLUE: {
    backgroundColor: "surface-accent-blue-subtler",
    iconColor: "icon-on-color-blue",
  },
  PURPLE: {
    backgroundColor: "surface-accent-purple-subtler",
    iconColor: "icon-on-color-purple",
  },
  PINK: {
    backgroundColor: "surface-accent-pink-subtler",
    iconColor: "icon-on-color-pink",
  },
  NEUTRAL: {
    backgroundColor: "surface-secondary",
    iconColor: "icon-primary",
  },
  WHITE: {
    backgroundColor: "surface-primary",
    iconColor: "icon-primary",
  },
  A: {
    backgroundColor: "surface-user-selectable-colorA-subtler",
    iconColor: "icon-on-color-user-selectable-a",
  },
  B: {
    backgroundColor: "surface-user-selectable-colorB-subtler",
    iconColor: "icon-on-color-user-selectable-b",
  },
  C: {
    backgroundColor: "surface-user-selectable-colorC-subtler",
    iconColor: "icon-on-color-user-selectable-c",
  },
  D: {
    backgroundColor: "surface-user-selectable-colorD-subtler",
    iconColor: "icon-on-color-user-selectable-d",
  },
  E: {
    backgroundColor: "surface-user-selectable-colorE-subtler",
    iconColor: "icon-on-color-user-selectable-e",
  },
  F: {
    backgroundColor: "surface-user-selectable-colorF-subtler",
    iconColor: "icon-on-color-user-selectable-f",
  },
};
